// ─── StravaConnect ────────────────────────────────────────────────────────────
import React, { useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import API_BASE_URL from '../../config/api';
import StravaLogo from '../../assets/Strava.png';

const API_URL = API_BASE_URL;
const OAUTH_URL = 'https://tzone-web-backend-1-2ev5.onrender.com';

const StravaConnect = ({ userId }) => {
  const [status,  setStatus]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [message, setMessage] = useState(null);
  const popupRef = useRef(null);
  const timerRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/strava/status/${userId}`);
      setStatus(res.data);
    } catch (e) {
      setMessage({ type: 'error', text: 'Error obteniendo estado de Strava' });
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchStatus();

    // Escuchar mensaje del popup cuando cierra
    const handleMessage = (event) => {
      if (event.data === 'strava-connected') {
        setMessage({ type: 'success', text: '¡Cuenta Strava conectada correctamente!' });
        fetchStatus();
      } else if (event.data === 'strava-error') {
        setMessage({ type: 'error', text: 'Error al conectar con Strava. Intenta nuevamente.' });
      } else if (event.data === 'strava-denied') {
        setMessage({ type: 'error', text: 'Autorización de Strava cancelada.' });
      }
    };
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [fetchStatus]);

  // ── Iniciar flujo OAuth en popup ──────────────────────────────────────────
  const handleConnect = () => {
    const popup = window.open(
      `${OAUTH_URL}/api/strava/connect/${userId}`,
      'strava-oauth',
      'width=600,height=700,scrollbars=yes,resizable=yes'
    );
    popupRef.current = popup;

    // Fallback: si el popup cierra sin enviar mensaje, refrescamos estado igual
    timerRef.current = setInterval(() => {
      if (popup && popup.closed) {
        clearInterval(timerRef.current);
        fetchStatus();
      }
    }, 1000);
  };

  // ── Desconectar ────────────────────────────────────────────────────────────
  const handleDisconnect = async () => {
    if (!window.confirm('¿Desconectar tu cuenta de Strava?')) return;
    try {
      await axios.post(`${API_URL}/api/strava/disconnect/${userId}`);
      setMessage({ type: 'success', text: 'Cuenta Strava desconectada' });
      fetchStatus();
    } catch (e) {
      setMessage({ type: 'error', text: 'Error desconectando Strava' });
    }
  };

  // ── Sync manual ────────────────────────────────────────────────────────────
  const handleSync = async () => {
    try {
      setSyncing(true);
      setMessage(null);
      const res = await axios.post(`${API_URL}/api/strava/sync/${userId}`, { hours: 168 });
      setMessage({ type: 'success', text: res.data.message || 'Sincronización completada.' });
      setTimeout(fetchStatus, 3000);
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.message || 'Error sincronizando Strava' });
    } finally {
      setSyncing(false);
    }
  };

  if (loading) return <div className="tz-conn-loading">Cargando...</div>;

  return (
    <>
      {message && (
        <div className={`tz-conn-message tz-conn-message--${message.type}`}>
          {message.text}
        </div>
      )}

      <div className={`tz-device-card ${status?.connected ? 'connected' : ''}`}>
        <div className="tz-device-logo tz-device-logo--light">
          <img src={StravaLogo} alt="Strava" />
        </div>
        <div className="tz-device-divider" />
        <div className="tz-device-info">
          <div className="tz-device-name">Strava</div>
          {status?.connected ? (
            <>
              <div className="tz-device-meta">
                {status.athleteName || 'Atleta conectado'}
                {status.athleteId && (
                  <span style={{ fontSize: '0.75em', color: '#888', marginLeft: '6px' }}>
                    · ID {status.athleteId}
                  </span>
                )}
              </div>
              <div className="tz-device-status">
                <span className="tz-status-dot on" />
                <span className="tz-status-label on">Conectado</span>
              </div>
              <div className="tz-sync-time">
                Última sincronización:{' '}
                {status.lastSync
                  ? new Date(status.lastSync).toLocaleString('es-CL')
                  : 'Nunca'}
              </div>
            </>
          ) : (
            <>
              <div className="tz-device-meta">No conectado</div>
              <div className="tz-device-status">
                <span className="tz-status-dot" />
                <span className="tz-status-label">Desconectado</span>
              </div>
            </>
          )}
        </div>
        <div className="tz-device-actions">
          {status?.connected ? (
            <>
              <button
                className="tz-btn tz-btn-outline"
                onClick={handleSync}
                disabled={syncing || status.rateLimited}
              >
                {syncing ? 'Sincronizando...' : 'Sincronizar'}
              </button>
              <button className="tz-btn tz-btn-danger" onClick={handleDisconnect}>
                Desconectar
              </button>
            </>
          ) : (
            <button className="tz-btn tz-btn-primary" onClick={handleConnect}>
              Conectar con Strava
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default StravaConnect;
